import { SciChartNestedOverview, SciChartReact } from "scichart-react";
import { useLocalization } from "@progress/kendo-react-intl";
import React, { CSSProperties, useEffect, useRef, ReactElement } from "react";
import { CursorModifier, ModifierMouseArgs, Point, RolloverModifier } from "scichart";
import { AppContext } from "../../../../../AppContext";
import { getCurrentLanguage } from "../../../../Shared/SciChart/ChartConfiguration";
import { overviewOptions, getResettableOverviewOptions } from "../../../../Shared/SciChart/Overview";
import useChartStore from "../../../../../Zustand/chartStore";
import { useAnomalyBandContext } from "../../../../Dashboards/Assets/Components/Overview/Dialogs/AnomalyDetector/AnomalyBand/AnomalyBandContext";
import { attachAnomalyBands } from "../../../../Dashboards/Assets/Components/Overview/Dialogs/AnomalyDetector/AnomalyBand/attachAnomalyBands";

export interface BaseChartEffect {
  type: 'changeChartType' | 'setIsSingleMode' | 'setAutoRange' | 'showThresholds' | 'changeDataSeries';
  dependency: string;
  apiMethod?: string; // API method name (optional, uses type by default)
  getValue?: (dependency: string, additionalChartOptions?: any) => any; // Custom value getter
}

export interface BaseChartProps {
  chartData: any;
  isSimpleChart?: boolean;
  chartParentHeight?: number;
  
  // Chart API function
  getChartAPI: (props: any, chartOptions: any, localizationService: any, currentLanguage: any) => any;
  
  // Chart identification
  chartKey: string;
  
  // Visible series configuration
  visibleSeriesStoreKey: string; // e.g., "visibleSeriesActivity"
  setVisibleSeriesStoreKey: string; // e.g., "setVisibleSeriesActivity"
  initialVisibleSeries?: { [key: string]: boolean }; // Default visible series state
  
  // Chart options from store
  useAutoRange?: boolean; // Default: true
  useViewType?: boolean; // Default: true
  useAggregator?: boolean; // Default: true
  useIsSinglePressureChart?: boolean; // Default: false
  useIsThresholdsOn?: boolean; // Default: false
  
  // Additional chart options
  additionalChartOptions?: Record<string, any>; // Extra options to pass to chartOptions
  
  // Effects configuration
  effects?: BaseChartEffect[];
  
  // Styling
  defaultHeight?: number | string; // Default: 500
  minHeight?: string; // Optional min height
  showOverview?: boolean; // Default: true (false for StatusChart)
  /** When true, double-clicking the overview resets the X range to full extent. Default: true */
  overviewDoubleClickReset?: boolean;
  wrapperClassName?: string; // Optional wrapper className
  
  // Special props for specific charts
  addHitTestRadius?: boolean;
  isFullscreen?: boolean;
  /** When false, skip attaching anomaly bands (e.g. for StatusChart) */
  attachAnomalyBands?: boolean;
  greasePumpData?: {
    serialNo: string;
    channelNo: number;
    timestamps: number[];
    values: number[];
  };
}

export default function BaseChart(props: BaseChartProps): ReactElement {
  const {
    chartData,
    isSimpleChart,
    chartParentHeight,
    getChartAPI,
    chartKey,
    visibleSeriesStoreKey,
    setVisibleSeriesStoreKey,
    initialVisibleSeries,
    useAutoRange = true,
    useViewType = true,
    useAggregator = true,
    useIsSinglePressureChart = false,
    useIsThresholdsOn = false,
    additionalChartOptions = {},
    effects = [],
    defaultHeight = 500,
    minHeight,
    showOverview = true,
    overviewDoubleClickReset = true,
    wrapperClassName,
    addHitTestRadius,
    isFullscreen,
    greasePumpData,
    attachAnomalyBands: attachAnomalyBandsProp = true
  } = props;

  const styles: { chartWrapper: CSSProperties } = {
    chartWrapper: {
      overflow: 'hidden',
      position: 'relative',
      touchAction: 'none',
      height: isSimpleChart ? "100%" : "85%",
      width: '100%'
    }
  };

  const localizationService = useLocalization();
  const { localeId } = React.useContext<any>(AppContext);
  const currentLanguage = getCurrentLanguage(localeId);
  
  // Always call all hooks - React Hooks rules require consistent order
  const isAutoRangeStore = useChartStore(state => state.isAutoRange);
  const viewTypeStore = useChartStore(state => state.viewType);
  const aggregatorStore = useChartStore(state => state.aggregator);
  const isSinglePressureChartStore = useChartStore(state => state.isSinglePressureChart);
  const isThresholdsOnStore = useChartStore(state => state.isThresholdsOn);
  const setVisibleRangeChartStore = useChartStore(state => state.setVisibleRange);
  
  // Use values conditionally based on props
  const isAutoRange = useAutoRange ? isAutoRangeStore : undefined;
  const viewType = useViewType ? viewTypeStore : undefined;
  const aggregator = useAggregator ? aggregatorStore : undefined;
  const isSinglePressureChart = useIsSinglePressureChart ? isSinglePressureChartStore : undefined;
  const isThresholdsOn = useIsThresholdsOn ? isThresholdsOnStore : undefined;
  
  // Dynamically get the setVisibleSeries function from store
  // We need to access it via getState() since we can't use dynamic keys in useChartStore selector
  const getSetVisibleSeriesFromStore = () => {
    const storeState = useChartStore.getState() as any;
    return storeState[setVisibleSeriesStoreKey] || (() => {});
  };
  
  const setVisibleSeriesChartStoreRef = useRef(getSetVisibleSeriesFromStore());
  
  // Update ref when store key changes
  useEffect(() => {
    setVisibleSeriesChartStoreRef.current = getSetVisibleSeriesFromStore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setVisibleSeriesStoreKey]);

  // Get initial visible series from store or use provided initial
  const getInitialVisibleSeries = () => {
    if (initialVisibleSeries) {
      return initialVisibleSeries;
    }
    try {
      const storeState = useChartStore.getState();
      const visibleSeries = (storeState as any)[visibleSeriesStoreKey];
      return visibleSeries || {};
    } catch {
      return {};
    }
  };

  const visibleSeriesRef = useRef<{ [key: string]: boolean }>(getInitialVisibleSeries());

  const setVisibleRange = (range: { min: number | undefined; max: number | undefined; }) => {
    setVisibleRangeChartStore(range.min, range.max);
  };

  const setVisibleSeries = (id: string, isVisible: boolean) => {
    visibleSeriesRef.current[id] = isVisible;
    setVisibleSeriesChartStoreRef.current(id, isVisible);
  };

  // Build chartOptions dynamically
  const chartOptions: any = {
    setVisibleRange: setVisibleRange,
    visibleRange: chartData.visibleRange,
    isSimpleChart: isSimpleChart,
    ...additionalChartOptions
  };

  // Add visible series configuration
  chartOptions[setVisibleSeriesStoreKey] = setVisibleSeries;
  chartOptions[visibleSeriesStoreKey] = visibleSeriesRef.current;

  // Add conditional options
  if (isAutoRange !== undefined) chartOptions.isAutoRange = isAutoRange;
  if (viewType !== undefined) chartOptions.viewType = viewType;
  if (aggregator !== undefined) chartOptions.aggregator = aggregator;
  if (isSinglePressureChart !== undefined) chartOptions.isSinglePressureChart = isSinglePressureChart;
  if (isThresholdsOn !== undefined) chartOptions.isThresholdsOn = isThresholdsOn;
  if (addHitTestRadius !== undefined) chartOptions.addHitTestRadius = addHitTestRadius;
  if (isFullscreen !== undefined) chartOptions.isFullscreen = isFullscreen;
  if (greasePumpData !== undefined) chartOptions.greasePumpData = greasePumpData;

  const [chartAPI] = React.useState(() => 
    getChartAPI({ chartData, ...additionalChartOptions }, chartOptions, localizationService, currentLanguage)
  );

  const anomalyBandContext = useAnomalyBandContext();
  const anomalyBandContextRef = useRef(anomalyBandContext);
  const anomalyCleanupRef = useRef<(() => void) | null>(null);
  const sciChartSurfaceRef = useRef<any>(null);
  const wasmContextRef = useRef<any>(null);
  const chartRootElementRef = useRef<string | HTMLDivElement | undefined>(undefined);
  const anomalyBandReadOnly = anomalyBandContext?.readOnly;

  useEffect(() => {
    anomalyBandContextRef.current = anomalyBandContext;
  }, [anomalyBandContext]);

  const initChart = React.useMemo(() => {
    return async (rootElement: string | HTMLDivElement) => {
      const result = await chartAPI.initChart(rootElement);
      const surface = result.sciChartSurface;
      const wasmContext = (result as any).wasmContext;
      sciChartSurfaceRef.current = surface;
      wasmContextRef.current = wasmContext;
      chartRootElementRef.current = rootElement;
      if (attachAnomalyBandsProp) {
        const currentAnomalyBandContext = anomalyBandContextRef.current;
        if (surface && currentAnomalyBandContext) {
          anomalyCleanupRef.current?.();
          anomalyCleanupRef.current = attachAnomalyBands(surface, wasmContext, currentAnomalyBandContext, rootElement);
        }
      }
      return result;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartAPI.initChart, attachAnomalyBandsProp]);

  useEffect(() => {
    if (!attachAnomalyBandsProp) {
      return;
    }

    const surface = sciChartSurfaceRef.current;
    const currentAnomalyBandContext = anomalyBandContextRef.current;

    if (!surface || !currentAnomalyBandContext) {
      return;
    }

    anomalyCleanupRef.current?.();
    anomalyCleanupRef.current = attachAnomalyBands(
      surface,
      wasmContextRef.current,
      currentAnomalyBandContext,
      chartRootElementRef.current
    );
  }, [attachAnomalyBandsProp, anomalyBandReadOnly]);

  const handleChartInit = React.useCallback(
    (initResult: any) => {
      chartAPI.configAfterInit?.(initResult);

      // Clear stale tooltips when the chart container resizes or the cursor
      // leaves the chart. Without this, RolloverModifier/CursorModifier keep
      // their cached mousePoint in old canvas coords and tooltips appear
      // "stuck" until the user re-hovers — and orphan tooltip annotations
      // from replaced series (e.g. on chartType change) never get hidden.
      let resizeObserver: ResizeObserver | undefined;
      let clearTooltips: (() => void) | undefined;
      const rootEl = chartRootElementRef.current;
      const surface = initResult?.sciChartSurface;
      if (typeof window !== "undefined" && rootEl && typeof rootEl !== "string" && surface) {
        clearTooltips = () => {
          const args = new ModifierMouseArgs(new Point(-1, -1));
          surface.chartModifiers.asArray().forEach((m: any) => {
            if (m instanceof RolloverModifier || m instanceof CursorModifier) {
              m.modifierMouseLeave?.(args);
            }
          });
          // modifierMouseLeave only clears tooltips of currently-included series.
          // Hide every modifier annotation directly to also catch orphans left
          // behind by renderableSeries.set() (chart-type swap, data reload, etc.).
          surface.modifierAnnotations?.asArray().forEach((ann: any) => {
            if (ann && typeof ann.isHidden === "boolean") {
              ann.isHidden = true;
            }
          });
          surface.invalidateElement?.();
        };
        resizeObserver = new ResizeObserver(clearTooltips);
        resizeObserver.observe(rootEl);
        rootEl.addEventListener("mouseleave", clearTooltips);
      }

      return () => {
        resizeObserver?.disconnect();
        if (clearTooltips && rootEl && typeof rootEl !== "string") {
          rootEl.removeEventListener("mouseleave", clearTooltips);
        }
        anomalyCleanupRef.current?.();
        anomalyCleanupRef.current = null;
        sciChartSurfaceRef.current = null;
        wasmContextRef.current = null;
        chartRootElementRef.current = undefined;
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chartAPI.configAfterInit]
  );

  // Setup effects dynamically
  useEffect(() => {
    if (chartAPI.changeChartType && viewType !== undefined) {
      const effect = effects.find(e => e.type === 'changeChartType');
      if (effect) {
        if (effect.dependency === 'visibleSeriesRef') {
          chartAPI.changeChartType(viewType, visibleSeriesRef.current);
        } else {
          chartAPI.changeChartType(viewType);
        }
      } else if (effects.length === 0) {
        // Default behavior if no effects specified but method exists
        chartAPI.changeChartType(viewType);
      }
    }
  }, [chartAPI, viewType, effects, visibleSeriesRef]);

  useEffect(() => {
    if (chartAPI.setIsSingleMode && isSinglePressureChart !== undefined) {
      chartAPI.setIsSingleMode(isSinglePressureChart);
    }
  }, [chartAPI, isSinglePressureChart]);

  useEffect(() => {
    if (chartAPI.setAutoRange && isAutoRange !== undefined) {
      chartAPI.setAutoRange(isAutoRange);
    }
  }, [chartAPI, isAutoRange]);

  useEffect(() => {
    if (chartAPI.showThresholds && isThresholdsOn !== undefined) {
      chartAPI.showThresholds(isThresholdsOn);
    }
  }, [chartAPI, isThresholdsOn]);

  // Handle custom effects
  useEffect(() => {
    const changeDataSeriesEffect = effects.find(e => e.type === 'changeDataSeries');
    if (changeDataSeriesEffect && chartAPI.changeDataSeries) {
      const value = changeDataSeriesEffect.getValue 
        ? changeDataSeriesEffect.getValue(changeDataSeriesEffect.dependency, additionalChartOptions)
        : additionalChartOptions[changeDataSeriesEffect.dependency];
      if (value !== undefined) {
        chartAPI.changeDataSeries(value);
      }
    }
  }, [chartAPI, effects, additionalChartOptions]);

  // Overview options: a fresh instance per mount when double-click reset is enabled
  // (the modifier is stateful and must not be shared across surfaces).
  const resolvedOverviewOptions = React.useMemo(
    () => (overviewDoubleClickReset ? getResettableOverviewOptions() : overviewOptions),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [overviewDoubleClickReset, chartKey]
  );

  // Calculate height
  const height = chartParentHeight || defaultHeight;
  const heightStyle = typeof height === 'number' ? `${height}px` : height;
  const styleProps: CSSProperties = {
    aspectRatio: showOverview ? 2 : 1,
    width: "100%",
    height: heightStyle,
    ...(minHeight && { minHeight })
  };

  // Special case for StatusChart (no overview, fixed height)
  if (!showOverview) {
    const statusHeight = (additionalChartOptions as any)?.showStatus === false ? "35px" : "65px";
    return (
      <div className={`status-chart-wrapper ${wrapperClassName || "flex"}`} style={{ position: 'relative', zIndex: 10 }}>
        <SciChartReact
          key={chartKey}
          onInit={handleChartInit}
          initChart={initChart}
          style={{ aspectRatio: 1, width: "100%", height: statusHeight }}
          fallback={<div/>}
        />
      </div>
    );
  }

  return isSimpleChart ? (
    <div style={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      overflow: 'hidden'
    }}>
      <SciChartReact
        key={chartKey}
        onInit={handleChartInit}
        initChart={initChart}
        style={styles.chartWrapper}
        innerContainerProps={{ style: { height: "100%" } }}
        fallback={<div/>}
      />
    </div>
  ) : (
    <div style={{ width: "100%" }} className={wrapperClassName}>
      <SciChartReact
        key={chartKey}
        onInit={handleChartInit}
        initChart={initChart}
        style={styleProps}
        innerContainerProps={{ style: { height: "85%" } }}
        fallback={<div/>}
      >
        <SciChartNestedOverview style={{ height: "14%" }} options={resolvedOverviewOptions}/>
      </SciChartReact>
    </div>
  );
}
